// Portfolio Administration v1.0 PR 4a §7.4 — "Add insurance policy" modal.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useCreateInsurancePolicy } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { UpsertInsurancePolicyRequest } from "@/api/portfolioAdmin/types";

interface AddInsurancePolicyModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
}

const POLICY_TYPES = [
  { value: "GeneralLiability", label: "General Liability (INS-1)" },
  { value: "Property", label: "Property (INS-2)" },
  { value: "BusinessInterruption", label: "Business Interruption (INS-3)" },
  { value: "KeyPersonLife", label: "Key Person Life (INS-4)" },
  { value: "KeyPersonDisability", label: "Key Person Disability (INS-5)" },
  { value: "Other", label: "Other" },
];

export function AddInsurancePolicyModal({
  loanId,
  open,
  onClose,
}: AddInsurancePolicyModalProps) {
  const toast = useToast();
  const create = useCreateInsurancePolicy(loanId);

  const [policyType, setPolicyType] = useState("GeneralLiability");
  const [carrier, setCarrier] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [coverage, setCoverage] = useState("");
  const [effective, setEffective] = useState("");
  const [expiration, setExpiration] = useState("");

  const reset = () => {
    setPolicyType("GeneralLiability");
    setCarrier("");
    setPolicyNumber("");
    setCoverage("");
    setEffective("");
    setExpiration("");
  };

  const onSubmit = async () => {
    if (!expiration) {
      toast.error("Expiration date is required.");
      return;
    }
    const body: UpsertInsurancePolicyRequest = {
      policyType,
      carrierName: carrier || null,
      policyNumber: policyNumber || null,
      coverageAmount: coverage ? Number(coverage) : null,
      effectiveDate: effective || null,
      expirationDate: expiration,
    };
    try {
      await create.mutateAsync(body);
      toast.success("Insurance policy added.");
      reset();
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not add policy.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Add insurance policy"
      description="Records carrier + coverage metadata. Renewal calendar items are auto-created based on the expiration date."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={create.isPending}
            data-testid="insurance-add-submit"
          >
            Add policy
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-space-3" data-testid="insurance-add-form">
        <label className="flex flex-col gap-space-1 text-caption col-span-2">
          <span className="text-gray-mid uppercase tracking-label">Policy type</span>
          <select
            className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
            value={policyType}
            onChange={(e) => setPolicyType(e.target.value)}
            data-testid="insurance-add-policyType"
          >
            {POLICY_TYPES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Carrier</span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            data-testid="insurance-add-carrier"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Policy #</span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small font-mono"
            value={policyNumber}
            onChange={(e) => setPolicyNumber(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Coverage ($)</span>
          <input
            type="number"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={coverage}
            onChange={(e) => setCoverage(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Effective</span>
          <input
            type="date"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Expiration <span className="text-status-error">*</span>
          </span>
          <input
            type="date"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={expiration}
            onChange={(e) => setExpiration(e.target.value)}
            required
            data-testid="insurance-add-expiration"
          />
        </label>
      </div>
    </Modal>
  );
}
